import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import { Badge, Card, ErrorAlert, Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow } from '@react/components';
import { mapAuditActionToVariant } from '@/features/document/mapStatusToVariant';
import { documentApi, userprofileApi } from '@core/api';

type ResolvedActor = { name: string; profileId: string };

const actionKeys: Record<string, string> = {
  CREATED: 'created', UPDATED: 'updated', STATUS_CHANGED: 'statusChanged',
  VERSION_CREATED: 'versionCreated', SUBMITTED_FOR_APPROVAL: 'submitted',
  APPROVED: 'approved', REJECTED: 'rejected', PUBLISHED: 'published',
  ARCHIVED: 'archived', RESTORED: 'restored', DELETED: 'deleted',
  ACKNOWLEDGMENT_REQUESTED: 'acknowledgmentRequested', ACKNOWLEDGED: 'acknowledged',
  OWNER_CHANGED: 'ownerChanged', CATEGORY_CHANGED: 'categoryChanged',
  DOWNLOADED: 'downloaded', VIEWED: 'viewed', PIPELINE_STARTED: 'pipelineStarted',
  PIPELINE_STAGE_COMPLETED: 'pipelineStageCompleted', PIPELINE_COMPLETED: 'pipelineCompleted',
  PIPELINE_CANCELLED: 'pipelineCancelled', PIPELINE_ACTION_REJECTED: 'pipelineActionRejected',
};

export const DocumentAuditLogPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useDocument();
  const { t, locale } = useI18n();
  const [entries, setEntries] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [actors, setActors] = useState<Record<string, ResolvedActor>>({});
  const [documentTitle, setDocumentTitle] = useState('');
  const [creatorId, setCreatorId] = useState('');

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    Promise.allSettled([store.ListAuditLog('document', id), documentApi.GetDocument(id)]).then(async ([auditResult, documentResult]) => {
      if (cancelled) return;
      if (auditResult.status === 'rejected') throw auditResult.reason;
      const response: any = auditResult.value;
      const document: any = documentResult.status === 'fulfilled' ? documentResult.value : null;
      const logs: any[] = response?.logs?.logs ?? [];
      setEntries(logs);
      setDocumentTitle(document?.document?.title || '');
      const creatorId = document?.document?.creator?.entity?.id;
      setCreatorId(creatorId || '');
      const actorIds = [...new Set(logs.map((entry) => entry.actor?.entity?.id || (entry.action === 'DOCUMENT_AUDIT_ACTION_CREATED' ? creatorId : '')).filter(Boolean))] as string[];
      const profiles = await Promise.all(actorIds.map(async (userId) => {
        try {
          const result = await userprofileApi.GetUserprofileDefault(userId);
          const profile = result?.userprofile;
          const name = profile?.display_name || [profile?.first_name, profile?.last_name].filter(Boolean).join(' ');
          return [userId, name && profile?.entity?.id ? { name, profileId: profile.entity.id } : null] as const;
        } catch {
          return [userId, null] as const;
        }
      }));
      if (!cancelled) setActors(Object.fromEntries(profiles.filter((pair): pair is readonly [string, ResolvedActor] => pair[1] !== null)));
    }).catch((err: unknown) => {
      if (!cancelled) setError(err);
    }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => { cancelled = true; };
  }, [id]);

  const getDetails = (item: any, oldValue: string, newValue: string): string => {
    const detail = String(item.details || '');
    if (detail.startsWith('document.fields:')) {
      const fields = detail.slice('document.fields:'.length).split(',').map((field) => t(`document.audit.fields.${field}`));
      return `${t('document.audit.changedFields')}: ${fields.join(', ')}`;
    }
    if (detail === 'blocks.created' || detail === 'blocks.changed') {
      const detailKey = detail.replace(/\.([a-z])/g, (_: string, letter: string) => letter.toUpperCase());
      return t(`document.audit.detailText.${detailKey}`, { count: Number(item.new_value || 0) });
    }
    if (detail) {
      const detailKey = detail.replace(/\.([a-z])/g, (_: string, letter: string) => letter.toUpperCase());
      return t(`document.audit.detailText.${detailKey}`);
    }
    if (item.action === 'DOCUMENT_AUDIT_ACTION_CREATED' && documentTitle) return `${t('document.audit.document')}: ${documentTitle}`;
    if (item.action === 'DOCUMENT_AUDIT_ACTION_ACKNOWLEDGED') return t('document.audit.detailText.acknowledged');
    if (oldValue || newValue) return t('document.audit.detailText.valueChanged');
    return t('document.audit.detailText.noDetails');
  };

  const valueLabel = (value: unknown): string => {
    if (typeof value !== 'string' || !value) return '';
    const ackStatus = value.replace(/^ACKNOWLEDGMENT_STATUS_/, '').toLowerCase();
    if (ackStatus !== value.toLowerCase()) return t(`document.ack.status.${ackStatus}`);
    return value;
  };

  return (
    <div className="space-y-5">
      <h1 className="sr-only">{t('document.auditLog')}</h1>
      <nav className="text-sm">
        <Link to="/documents" className="text-muted-foreground hover:text-foreground">{t('document.list')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/documents/${id}`} className="text-muted-foreground hover:text-foreground">{t('document.details')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span>{t('document.auditLog')}</span>
      </nav>
      {Boolean(error) && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}
      {!error && <Card>
        <Table hoverable>
          <TableHeader><TableRow>
            <TableHead>{t('document.audit.action')}</TableHead>
            <TableHead>{t('document.audit.who')}</TableHead>
            <TableHead>{t('document.audit.details')}</TableHead>
            <TableHead>{t('document.audit.when')}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {isLoading ? <TableRow><TableCell colSpan={4} className="py-8 text-center">{t('common.loading')}</TableCell></TableRow>
              : entries.length === 0 ? <TableEmpty colSpan={4} message={t('document.noAuditEntries')} />
                : entries.map((item: any) => {
                  const action = String(item.action || '');
                  const key = actionKeys[action.replace(/^DOCUMENT_AUDIT_ACTION_/, '')];
                  const oldValue = valueLabel(item.old_value);
                  const newValue = valueLabel(item.new_value);
                  const actorId = item.actor?.entity?.id || (action === 'DOCUMENT_AUDIT_ACTION_CREATED' ? creatorId : '');
                  const actor = actors[actorId];
                  const date = item.created_at ? new Date(item.created_at) : null;
                  const variant = mapAuditActionToVariant(action);
                  return <TableRow key={item.entity?.id || `${action}-${item.created_at}`}>
                    <TableCell className="align-top">
                      <Badge variant={variant === 'info' ? 'default' : variant} size="sm">
                        {key ? t(`document.audit.actions.${key}`) : t('document.audit.actions.other')}
                      </Badge>
                    </TableCell>
                    <TableCell className="align-top text-sm whitespace-nowrap">
                      {actor ? <Link to={`/userprofiles/${actor.profileId}`} className="font-medium text-primary hover:underline">{actor.name}</Link>
                        : <span className="text-muted-foreground">{actorId ? t('document.audit.actorUnavailable') : t('document.audit.actorNotRecorded')}</span>}
                    </TableCell>
                    <TableCell className="align-top min-w-0 max-w-[36rem]">
                      <div className="text-sm text-foreground break-words">{getDetails(item, oldValue, newValue)}</div>
                      {(oldValue || newValue) && !['blocks.created', 'blocks.changed'].includes(item.details) &&
                        <div className="mt-1 text-xs text-muted-foreground break-words">{oldValue && `${oldValue} → `}{newValue}</div>}
                    </TableCell>
                    <TableCell className="align-top whitespace-nowrap text-sm text-muted-foreground">
                      {date && !Number.isNaN(date.getTime()) ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date) : '—'}
                    </TableCell>
                  </TableRow>;
                })}
          </TableBody>
        </Table>
      </Card>}
    </div>
  );
});

export default DocumentAuditLogPage;
